import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { IMessage, IMessageImage, StopType } from "@typing/wsui/getMessages";
import { ConversationSceneType } from "@typing/wsui/msg";

/**
 * 单个会话的数据
 */
interface ISessionData {
    messages: IMessage[]; // 消息列表
    pageTitle: string; // 页面标题
    hasMore: boolean; // 是否有更多历史消息
    sseLoading: boolean; // SSE 对话加载状态
    expandedThinking: Record<string, boolean>; // 思维链展开状态，key 为 segmentKey（messageId_segmentIdx）
}

const DEFAULT_SESSION: ISessionData = {
    messages: [],
    pageTitle: "对话详情",
    hasMore: false,
    sseLoading: false,
    expandedThinking: {},
};

/**
 * 聊天状态管理 Store
 */
interface IChatStore {
    // ========== 多会话数据存储 ==========
    sessions: Record<string, ISessionData>; // 按 sessionId 索引的会话数据
    currentSessionId: string | null; // 当前活跃会话 ID

    // ========== 会话数据操作 ==========
    getSession: (sessionId: string) => ISessionData | undefined;
    getLastBotMessageId: (sessionId: string) => string | undefined;
    getLastMessageId: (sessionId: string) => string | undefined;
    initSession: (sessionId: string, tempSessionId?: string) => void;
    clearSession: (sessionId: string) => void;
    setCurrentSession: (sessionId: string | null) => void;

    // ========== 消息操作 ==========
    addMessage: (sessionId: string, message: IMessage) => void;
    updateMessage: (sessionId: string, messageId: string, updater: (msg: IMessage) => IMessage) => void;
    removeMessage: (sessionId: string, messageId: string) => void;
    prependMessages: (sessionId: string, messages: IMessage[]) => void;

    // ========== 会话元信息 ==========
    setMessages: (sessionId: string, messages: IMessage[]) => void;
    setPageTitle: (sessionId: string, title: string) => void;
    setHasMore: (sessionId: string, hasMore: boolean) => void;
    setSSELoading: (sessionId: string, loading: boolean) => void;

    // ========== 思维链展开状态 ==========
    getExpandedThinking: (sessionId: string) => Record<string, boolean>;
    setExpandedThinking: (sessionId: string, expanded: Record<string, boolean>) => void;
    toggleExpandedThinking: (sessionId: string, segmentKey: string) => void;
    expandThinking: (sessionId: string, segmentKey: string) => void;
    collapseThinking: (sessionId: string, segmentKey: string) => void;
    batchSetExpandedThinking: (sessionId: string, updates: Record<string, boolean>) => void;

    // ========== 消息状态更新 ==========
    markMessageAsUserCancelled: (sessionId: string, messageId: string, stopType?: StopType) => void;

    // ========== 工具状态更新 ==========
    updateThinkProcessV2ReferenceStatus: (
        sessionId: string,
        messageId: string,
        segmentIdx: string,
        referenceId: string,
        toolType: "like" | "unlike",
        newStatus: number
    ) => void;
    updateToolStatus: (
        sessionId: string,
        messageId: string,
        fragmentId: string,
        toolType: "like" | "unlike",
        newStatus: number
    ) => void;

    // ========== 跨组件通信 ==========
    pendingQuery: {
        query: string;
        sceneType: ConversationSceneType;
        images?: IMessageImage[];
        timestamp: number;
    } | null;
    newSessionCreated: { sessionId: string; timestamp: number } | null;
    sessionTitleUpdated: {
        sessionId: string;
        title: string;
        timestamp: number;
    } | null;
    sseCompleted: { sessionId: string; timestamp: number } | null;

    setPendingQuery: (query: string, sceneType: ConversationSceneType, images?: IMessageImage[]) => void;
    clearPendingQuery: () => void;
    notifyNewSession: (sessionId: string) => void;
    clearNewSessionNotification: () => void;
    notifySessionTitleUpdate: (sessionId: string, title: string) => void;
    clearSessionTitleNotification: () => void;
    notifySSECompleted: (sessionId: string) => void;
    clearSSECompletedNotification: () => void;

    // ========== 融合模式全局状态（coredata 驱动） ==========
    coreSessionId: string;
    coreDataLoaded: boolean;
    setCoreSession: (sessionId: string) => void;
    resetCoreSession: () => void;
}

/**
 * 聊天状态管理 Hook
 */
export const useChatStore = create<IChatStore>()(
    immer((set, get) => ({
        // ========== 多会话数据存储 ==========
        sessions: {},
        currentSessionId: null,

        // ========== 会话数据操作 ==========
        /**
         * 获取指定会话的数据
         * @param sessionId - 会话 ID
         * @returns 会话数据对象，如果会话不存在则返回 undefined
         */
        getSession: (sessionId: string) => {
            return get().sessions[sessionId];
        },

        /**
         * 获取指定会话中最后一条 bot 消息的 ID
         * 用于取消 SSE 对话时获取 response_msg_id
         * @param sessionId - 会话 ID
         * @returns 最后一条 bot 消息的 message_id，如果不存在则返回 undefined
         */
        getLastBotMessageId: (sessionId: string) => {
            const session = get().sessions[sessionId];
            if (!session || session.messages.length === 0) {
                return undefined;
            }
            // 从后往前找第一条 bot 消息
            const lastBotMessage = [...session.messages].reverse().find((m) => m.poster.role === "bot");
            return lastBotMessage?.message_id;
        },

        /**
         * 获取指定会话中最后一条消息的 ID
         * 用于判断追问是否显示：只有当前消息是最后一条消息时才显示追问
         * 点击追问后发送新消息，原消息不再是最后一条，追问自动消失
         * @param sessionId - 会话 ID
         * @returns 最后一条消息的 message_id，如果不存在则返回 undefined
         */
        getLastMessageId: (sessionId: string) => {
            const session = get().sessions[sessionId];
            if (!session || session.messages.length === 0) {
                return undefined;
            }
            return session.messages[session.messages.length - 1].message_id;
        },

        /**
         * 初始化会话数据
         * 如果会话不存在，创建一个空的会话对象；如果已存在则不做任何操作
         * @param sessionId - 会话 ID
         * @param tempSessionId - 可选，临时会话 ID，如果提供则将临时会话的数据原封不动地复制到新会话
         */
        initSession: (sessionId: string, tempSessionId?: string) => {
            set((draft) => {
                if (!draft.sessions[sessionId]) {
                    if (tempSessionId && draft.sessions[tempSessionId]) {
                        // 从临时会话复制数据
                        draft.sessions[sessionId] = { ...draft.sessions[tempSessionId] };
                    } else {
                        draft.sessions[sessionId] = { ...DEFAULT_SESSION, expandedThinking: {} };
                    }
                }
            });
        },

        /**
         * 清除指定会话的所有数据
         * 从 Store 中删除该会话，包括消息、标题等所有信息
         * @param sessionId - 会话 ID
         */
        clearSession: (sessionId: string) => {
            set((draft) => {
                delete draft.sessions[sessionId];
            });
        },

        /**
         * 设置当前活跃的会话
         * @param sessionId - 会话 ID，传入 null 表示清除当前会话
         */
        setCurrentSession: (sessionId: string | null) => {
            set((draft) => {
                draft.currentSessionId = sessionId;
            });
        },

        // ========== 消息操作 ==========
        /**
         * 设置会话的完整消息列表
         * 会覆盖该会话原有的所有消息，如果会话不存在会自动初始化
         * @param sessionId - 会话 ID
         * @param messages - 新的消息列表
         */
        setMessages: (sessionId: string, messages: IMessage[]) => {
            set((draft) => {
                if (!draft.sessions[sessionId]) {
                    draft.sessions[sessionId] = { ...DEFAULT_SESSION, expandedThinking: {} };
                }
                draft.sessions[sessionId].messages = messages;
            });
        },

        /**
         * 向会话的消息列表末尾添加一条新消息
         * 如果会话不存在会自动初始化，常用于接收新的 AI 回复或用户输入
         * @param sessionId - 会话 ID
         * @param message - 要添加的消息对象
         */
        addMessage: (sessionId: string, message: IMessage) => {
            set((draft) => {
                if (!draft.sessions[sessionId]) {
                    draft.sessions[sessionId] = { ...DEFAULT_SESSION, expandedThinking: {} };
                }
                const messages = draft.sessions[sessionId].messages;
                if (!messages.some((m) => m.message_id === message.message_id)) {
                    messages.push(message);
                }
            });
        },

        /**
         * 更新指定会话中的某条消息
         * 使用 updater 函数进行更新，常用于 SSE 流式更新消息内容
         * @param sessionId - 会话 ID
         * @param messageId - 消息 ID
         * @param updater - 更新函数，接收原消息对象，返回新消息对象
         */
        updateMessage: (sessionId: string, messageId: string, updater: (msg: IMessage) => IMessage) => {
            set((draft) => {
                const session = draft.sessions[sessionId];
                if (!session) return;
                const idx = session.messages.findIndex((msg) => msg.message_id === messageId);
                if (idx !== -1) {
                    session.messages[idx] = updater(session.messages[idx]);
                }
            });
        },

        removeMessage: (sessionId: string, messageId: string) => {
            set((draft) => {
                const session = draft.sessions[sessionId];
                if (!session) return;
                session.messages = session.messages.filter((msg) => msg.message_id !== messageId);
            });
        },

        /**
         * 在会话消息列表头部批量插入历史消息
         * 常用于加载更多历史记录场景，保持时间顺序（旧消息在前）
         * @param sessionId - 会话 ID
         * @param messages - 要插入的历史消息列表
         */
        prependMessages: (sessionId: string, messages: IMessage[]) => {
            set((draft) => {
                const session = draft.sessions[sessionId];
                if (!session) return;
                session.messages.unshift(...messages);
            });
        },

        // ========== 会话元信息 ==========
        /**
         * 设置会话的页面标题
         * 如果会话不存在会自动初始化，标题会显示在页面顶部和侧边栏
         * @param sessionId - 会话 ID
         * @param title - 页面标题文本
         */
        setPageTitle: (sessionId: string, title: string) => {
            set((draft) => {
                if (!draft.sessions[sessionId]) {
                    draft.sessions[sessionId] = { ...DEFAULT_SESSION, expandedThinking: {} };
                }
                draft.sessions[sessionId].pageTitle = title;
            });
        },

        /**
         * 设置会话是否还有更多历史消息可加载
         * 用于控制"加载更多"按钮的显示状态
         * @param sessionId - 会话 ID
         * @param hasMore - true 表示还有历史消息，false 表示已加载全部
         */
        setHasMore: (sessionId: string, hasMore: boolean) => {
            set((draft) => {
                const session = draft.sessions[sessionId];
                if (!session) return;
                session.hasMore = hasMore;
            });
        },

        /**
         * 设置会话的 SSE 加载状态
         * 用于控制输入框禁用状态和加载提示
         * @param sessionId - 会话 ID
         * @param loading - true 表示 SSE 对话进行中，false 表示已结束
         */
        setSSELoading: (sessionId: string, loading: boolean) => {
            set((draft) => {
                const session = draft.sessions[sessionId];
                if (!session) return;
                session.sseLoading = loading;
            });
        },

        // ========== 思维链展开状态 ==========
        /**
         * 获取会话的思维链展开状态
         * @param sessionId - 会话 ID
         * @returns 展开状态对象，key 为 segmentKey
         */
        getExpandedThinking: (sessionId: string) => {
            const session = get().sessions[sessionId];
            return session?.expandedThinking || {};
        },

        /**
         * 设置会话的完整思维链展开状态
         * @param sessionId - 会话 ID
         * @param expanded - 展开状态对象
         */
        setExpandedThinking: (sessionId: string, expanded: Record<string, boolean>) => {
            set((draft) => {
                const session = draft.sessions[sessionId];
                if (!session) return;
                session.expandedThinking = expanded;
            });
        },

        /**
         * 切换指定思维链的展开/收起状态
         * 惰性策略：undefined 视为收起，只有 true 才视为展开
         * @param sessionId - 会话 ID
         * @param segmentKey - segmentKey（messageId_segmentIdx）
         */
        toggleExpandedThinking: (sessionId: string, segmentKey: string) => {
            set((draft) => {
                const session = draft.sessions[sessionId];
                if (!session) return;
                const current = session.expandedThinking[segmentKey] === true;
                session.expandedThinking[segmentKey] = !current;
            });
        },

        /**
         * 展开指定思维链
         * @param sessionId - 会话 ID
         * @param segmentKey - segmentKey（messageId_segmentIdx）
         */
        expandThinking: (sessionId: string, segmentKey: string) => {
            set((draft) => {
                const session = draft.sessions[sessionId];
                if (!session) return;
                session.expandedThinking[segmentKey] = true;
            });
        },

        /**
         * 收起指定思维链
         * @param sessionId - 会话 ID
         * @param segmentKey - segmentKey（messageId_segmentIdx）
         */
        collapseThinking: (sessionId: string, segmentKey: string) => {
            set((draft) => {
                const session = draft.sessions[sessionId];
                if (!session) return;
                session.expandedThinking[segmentKey] = false;
            });
        },

        /**
         * 批量设置思维链展开状态
         * @param sessionId - 会话 ID
         * @param updates - 要更新的状态对象
         */
        batchSetExpandedThinking: (sessionId: string, updates: Record<string, boolean>) => {
            set((draft) => {
                const session = draft.sessions[sessionId];
                if (!session) return;
                Object.assign(session.expandedThinking, updates);
            });
        },

        // ========== 消息状态更新 ==========
        /**
         * 更新消息的 stop_type 状态
         * @param sessionId - 会话 ID
         * @param messageId - 消息 ID
         * @param stopType - 停止类型，默认为 "user_cancel"，也可能是 "max_token" 等
         */
        markMessageAsUserCancelled: (sessionId: string, messageId: string, stopType: StopType = "user_cancel") => {
            set((draft) => {
                const session = draft.sessions[sessionId];
                if (!session) return;
                const msg = session.messages.find((m) => m.message_id === messageId);
                if (msg) {
                    msg.stop_type = stopType;
                }
            });
        },

        // ========== 工具状态更新 ==========
        /**
         * 更新 ThinkProcessV2 引用文献的工具状态（点赞/点踩）
         * @param sessionId - 会话 ID
         * @param messageId - 消息 ID
         * @param segmentIdx - segment 索引（thinkProcessV2 的 segment_idx）
         * @param referenceId - 引用文献 ID
         * @param toolType - 工具类型，'like' 或 'unlike'
         * @param newStatus - 新状态值，通常 0 表示未选中，1 表示选中
         */
        updateThinkProcessV2ReferenceStatus: (
            sessionId: string,
            messageId: string,
            segmentIdx: string,
            referenceId: string,
            toolType: "like" | "unlike",
            newStatus: number
        ) => {
            set((draft) => {
                const session = draft.sessions[sessionId];
                if (!session) return;
                const msg = session.messages.find((m) => m.message_id === messageId);
                if (!msg) return;

                for (const section of msg.sections) {
                    for (const segment of section.segments) {
                        if (
                            segment.segment_idx !== segmentIdx ||
                            (segment.type !== 'thinkProcessV2' && segment.type !== 'oeReferences')
                        ) {
                            continue;
                        }
                        try {
                            const data = JSON.parse(segment.content);
                            const updatedReferences = data.references?.map((ref: any) => {
                                const refFragmentId = ref.tool_tip?.fragment_id || ref.id;
                                if (refFragmentId !== referenceId) return ref;
                                return {
                                    ...ref,
                                    tool_tip: {
                                        ...ref.tool_tip,
                                        tools:
                                            ref.tool_tip?.tools?.map((tool: any) => {
                                                if (tool.type === toolType) {
                                                    return { ...tool, status: newStatus };
                                                }
                                                // 互斥：点赞时取消点踩，点踩时取消点赞
                                                if (
                                                    newStatus === 1 &&
                                                    ((toolType === "like" && tool.type === "unlike") ||
                                                        (toolType === "unlike" && tool.type === "like"))
                                                ) {
                                                    return { ...tool, status: 0 };
                                                }
                                                return tool;
                                            }) || [],
                                    },
                                };
                            });
                            segment.content = JSON.stringify({
                                ...data,
                                references: updatedReferences,
                            });
                        } catch (e) {
                            console.error("Failed to parse thinkProcessV2 content:", e);
                        }
                    }
                }
            });
        },

        /**
         * 更新消息片段的工具状态（点赞/点踩）
         * 会自动处理互斥逻辑：选中点赞时重置点踩，反之亦然
         * @param sessionId - 会话 ID
         * @param messageId - 消息 ID
         * @param fragmentId - 片段 ID（segment_idx）
         * @param toolType - 工具类型，'like' 或 'unlike'
         * @param newStatus - 新状态值，通常 0 表示未选中，1 表示选中
         */
        updateToolStatus: (sessionId, messageId, fragmentId, toolType, newStatus) => {
            set((draft) => {
                const session = draft.sessions[sessionId];
                if (!session) return;
                const msg = session.messages.find((m) => m.message_id === messageId);
                if (!msg) return;

                for (const section of msg.sections) {
                    for (const segment of section.segments) {
                        if (segment.segment_idx !== fragmentId) continue;
                        if (segment.tool_tip?.tools) {
                            for (const tool of segment.tool_tip.tools) {
                                if (tool.type === toolType) {
                                    tool.status = newStatus;
                                } else if (newStatus === 1 && (tool.type === "like" || tool.type === "unlike")) {
                                    tool.status = 0;
                                }
                            }
                        }
                    }
                }
            });
        },

        // ========== 跨组件通信（保留原有） ==========
        pendingQuery: null,
        newSessionCreated: null,
        sessionTitleUpdated: null,
        sseCompleted: null,

        /**
         * 设置待处理的查询
         * 用于跨组件传递用户输入的问题，附带时间戳用于去重判断
         * @param query - 用户输入的查询文本
         */
        setPendingQuery: (query: string, sceneType: ConversationSceneType, images?: IMessageImage[]) => {
            set((draft) => {
                draft.pendingQuery = { query, sceneType, images, timestamp: Date.now() };
            });
        },
        /**
         * 清除待处理的查询
         * 问题已被消费后调用，避免重复处理
         */
        clearPendingQuery: () => {
            set((draft) => {
                draft.pendingQuery = null;
            });
        },

        /**
         * 通知有新会话被创建
         * 用于触发侧边栏刷新历史列表，附带时间戳用于去重判断
         * @param sessionId - 新创建的会话 ID
         */
        notifyNewSession: (sessionId: string) => {
            set((draft) => {
                draft.newSessionCreated = { sessionId, timestamp: Date.now() };
            });
        },
        /**
         * 清除新会话创建通知
         * 通知已被消费后调用，避免重复处理
         */
        clearNewSessionNotification: () => {
            set((draft) => {
                draft.newSessionCreated = null;
            });
        },

        /**
         * 通知会话标题已更新
         * 用于触发侧边栏同步标题显示，附带时间戳用于去重判断
         * @param sessionId - 会话 ID
         * @param title - 新的标题文本
         */
        notifySessionTitleUpdate: (sessionId: string, title: string) => {
            set((draft) => {
                draft.sessionTitleUpdated = { sessionId, title, timestamp: Date.now() };
            });
        },
        /**
         * 清除会话标题更新通知
         * 通知已被消费后调用，避免重复处理
         */
        clearSessionTitleNotification: () => {
            set((draft) => {
                draft.sessionTitleUpdated = null;
            });
        },

        /**
         * 通知 SSE 对话已完成
         * 用于触发侧边栏刷新历史列表（因为对话完成后顺序可能变化）
         * @param sessionId - 完成对话的会话 ID
         */
        notifySSECompleted: (sessionId: string) => {
            set((draft) => {
                draft.sseCompleted = { sessionId, timestamp: Date.now() };
            });
        },
        /**
         * 清除 SSE 完成通知
         * 通知已被消费后调用，避免重复处理
         */
        clearSSECompletedNotification: () => {
            set((draft) => {
                draft.sseCompleted = null;
            });
        },

        // ========== 融合模式全局状态 ==========
        coreSessionId: "",
        coreDataLoaded: false,

        setCoreSession: (sessionId: string) => {
            set((draft) => {
                draft.coreSessionId = sessionId;
                draft.coreDataLoaded = true;
            });
        },

        resetCoreSession: () => {
            set((draft) => {
                draft.coreSessionId = "";
                draft.coreDataLoaded = false;
            });
        },
    }))
);
